import { ref, computed } from 'vue';
import type { Ref } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { useFormBuilderToast } from '../composables/useFormBuilderToast';
import { positionApi } from '@/entities/Position';
import { departmentApi } from '@/entities/Department';
import { userApi } from '@/entities/User';
import { membersApi } from '@/entities/Branch';
import { fetchAllPages } from '@/shared/lib';
import type {
  FormElement,
  AlternativeItem,
  DropdownProperties,
  DropdownListType,
} from './form.types';

interface UseDropdownAlternativesModalOptions {
  selectedElement: Ref<FormElement | null | undefined>;
  updateElement: (updates: Partial<FormElement>) => void;
}

export function useDropdownAlternativesModal(options: UseDropdownAlternativesModalOptions) {
  const { selectedElement, updateElement } = options;
  const { showErrorToast } = useFormBuilderToast();

  const listType = ref<DropdownListType>('LIVRE');
  const localAlternatives = ref<AlternativeItem[]>([]);
  const searchQuery = ref('');
  const remoteItems = ref<AlternativeItem[]>([]);
  const isLoadingRemote = ref(false);

  const totalCount = computed(() => localAlternatives.value.length);

  const selectedRemoteIds = computed(() => new Set(localAlternatives.value.map((a) => a.id)));

  const allSelected = computed(
    () =>
      remoteItems.value.length > 0 &&
      remoteItems.value.every((item) => selectedRemoteIds.value.has(item.id))
  );
  let fallbackIdCounter = 0;

  function createAlternativeId(): string {
    const cryptoApi = globalThis.crypto;

    if (cryptoApi?.randomUUID) {
      return `alt-${cryptoApi.randomUUID()}`;
    }

    if (cryptoApi?.getRandomValues) {
      const bytes = new Uint8Array(16);
      cryptoApi.getRandomValues(bytes);
      const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
      return `alt-${hex}`;
    }

    fallbackIdCounter += 1;
    return `alt-fallback-${Date.now()}-${fallbackIdCounter}`;
  }

  function initFromElement() {
    const props = selectedElement.value?.properties as DropdownProperties | undefined;
    listType.value = props?.listType ?? 'LIVRE';
    localAlternatives.value = [
      ...(props?.alternatives ?? [{ id: 'alt-default', value: 'Opção 1' }]),
    ];
    searchQuery.value = '';
    remoteItems.value = [];
    if (listType.value !== 'LIVRE') {
      void fetchRemoteItems();
    }
  }

  function addAlternative() {
    const newItem: AlternativeItem = {
      id: createAlternativeId(),
      value: `Opção ${localAlternatives.value.length + 1}`,
    };
    localAlternatives.value = [...localAlternatives.value, newItem];
  }

  function removeAlternative(index: number) {
    if (localAlternatives.value.length <= 1) return;
    localAlternatives.value = localAlternatives.value.filter((_, i) => i !== index);
  }

  function updateAlternative(index: number, value: string) {
    localAlternatives.value = localAlternatives.value.map((alt, i) =>
      i === index ? { ...alt, value } : alt
    );
  }

  const fetchRemoteItemsDebounced = useDebounceFn(() => {
    void fetchRemoteItems();
  }, 300);

  async function fetchRemoteItems() {
    if (listType.value === 'LIVRE') return;
    isLoadingRemote.value = true;
    try {
      let items: AlternativeItem[] = [];
      const search = searchQuery.value || undefined;

      if (listType.value === 'CARGOS') {
        const positions = await fetchAllPages((page) =>
          positionApi.searchPositions({ search, page })
        );
        items = positions.map((p) => ({ id: String(p.id), value: p.name }));
      } else if (listType.value === 'DEPARTAMENTOS') {
        const departments = await fetchAllPages((page) =>
          departmentApi.search({ search, page })
        );
        items = departments.map((d) => ({ id: String(d.id), value: d.name ?? d.code }));
      } else if (listType.value === 'USUARIOS') {
        const users = await fetchAllPages((page) => userApi.search({ search, page }));
        items = users.map((u) => ({ id: String(u.id), value: u.name ?? '' }));
      } else if (listType.value === 'MEMBROS') {
        const members = await fetchAllPages((page) => membersApi.getMembers(page, 100, search));
        items = members
          .filter((m) => m.user?.id != null)
          .map((m) => ({ id: String(m.user!.id), value: m.user!.name ?? '' }));
      }

      remoteItems.value = items;
    } catch {
      showErrorToast({
        title: 'Erro ao carregar alternativas. Tente novamente.',
        type: 'error',
        hideIcon: true,
      });
      remoteItems.value = [];
    } finally {
      isLoadingRemote.value = false;
    }
  }

  function toggleItem(item: AlternativeItem) {
    if (selectedRemoteIds.value.has(item.id)) {
      localAlternatives.value = localAlternatives.value.filter((a) => a.id !== item.id);
    } else {
      localAlternatives.value = [...localAlternatives.value, item];
    }
  }

  function toggleSelectAll() {
    if (allSelected.value) {
      localAlternatives.value = [];
    } else {
      localAlternatives.value = [...remoteItems.value];
    }
  }

  function onListTypeChange(newType: DropdownListType) {
    listType.value = newType;
    searchQuery.value = '';
    if (newType === 'LIVRE') {
      localAlternatives.value = [{ id: createAlternativeId(), value: 'Opção 1' }];
      remoteItems.value = [];
    } else {
      localAlternatives.value = [];
      void fetchRemoteItems();
    }
  }

  function save() {
    const currentProps = selectedElement.value?.properties as DropdownProperties;
    updateElement({
      properties: {
        ...currentProps,
        alternatives: localAlternatives.value,
        listType: listType.value,
      } as DropdownProperties,
    });
  }

  return {
    listType,
    localAlternatives,
    searchQuery,
    remoteItems,
    isLoadingRemote,
    totalCount,
    selectedRemoteIds,
    allSelected,
    initFromElement,
    addAlternative,
    removeAlternative,
    updateAlternative,
    fetchRemoteItems: fetchRemoteItemsDebounced,
    toggleItem,
    toggleSelectAll,
    onListTypeChange,
    save,
  };
}
